import { useState } from 'react'
import { Button } from '../ui'
import { Label } from '../ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui'
import { Popover, PopoverContent, PopoverTrigger } from '../ui'
import { Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface UserOption {
  id: string
  username: string
  email: string
}

interface AddExistingGroupOwnerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: string
  users: UserOption[]
  loading?: boolean
  onAddUser: (userId: string, groupId: string, asAdmin: boolean) => Promise<void>
  onSearchChange?: (search: string) => void
}

export function AddExistingGroupOwnerModal({
  open,
  onOpenChange,
  groupId,
  users,
  loading = false,
  onAddUser,
  onSearchChange,
}: AddExistingGroupOwnerModalProps) {
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [searchValue, setSearchValue] = useState('')
  const [comboboxOpen, setComboboxOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const selectedUser = users.find((user) => user.id === selectedUserId)

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    if (onSearchChange) {
      onSearchChange(value)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedUserId || isSubmitting) {
      return
    }

    setIsSubmitting(true)
    try {
      await onAddUser(selectedUserId, groupId, true)
      onOpenChange(false)
      setSelectedUserId('')
      setSearchValue('')
    } catch (_error) {
      // Error handling is done by parent component
    } finally {
      setIsSubmitting(false)
    }
  }

  // Reset form when modal opens/closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSelectedUserId('')
      setSearchValue('')
      setComboboxOpen(false)
    }
    onOpenChange(newOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Assign user - Group Owner</DialogTitle>
          <DialogDescription>Search and select a user to add as a group owner.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="user-search">Search User</Label>
              <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={comboboxOpen}
                    className="justify-between"
                    disabled={loading}
                  >
                    {selectedUser
                      ? `${selectedUser.username} (${selectedUser.email})`
                      : 'Select user...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search users..."
                      value={searchValue}
                      onValueChange={handleSearchChange}
                    />
                    <CommandList>
                      <CommandEmpty>{loading ? 'Loading...' : 'No users found.'}</CommandEmpty>
                      <CommandGroup>
                        {users
                          .filter((user) =>
                            user.username.toLowerCase().includes(searchValue.toLowerCase()),
                          )
                          .map((user) => (
                            <CommandItem
                              key={user.id}
                              value={`${user.username} ${user.email}`}
                              onSelect={() => {
                                setSelectedUserId(user.id === selectedUserId ? '' : user.id)
                                setComboboxOpen(false)
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  selectedUserId === user.id ? 'opacity-100' : 'opacity-0',
                                )}
                              />
                              <div>
                                <div className="font-medium">{user.username}</div>
                                <div className="text-sm text-muted-foreground">{user.email}</div>
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              className="rounded-md"
              variant="main_outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              variant="main_action"
              className="rounded-md"
              type="submit"
              disabled={!selectedUserId || isSubmitting || loading}
            >
              {isSubmitting ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
